/**
 * Converting between the amounts people type or read and Stripe amounts.
 *
 * Stripe quotes `unit_amount` in a currency's smallest unit. For most that is
 * hundredths, but a zero-decimal currency has no minor unit at all — ¥1000 is
 * `unit_amount: 1000`, not 100000. Dividing those by 100 shows a price 100×
 * too small while Checkout charges the real amount, so every money formatter
 * has to ask the currency rather than assume two decimal places.
 *
 * Aliases: zero decimal currency, stripe amount, minor units
 */

/** https://docs.stripe.com/currencies#zero-decimal */
const ZERO_DECIMAL_CURRENCIES = new Set([
  "bif",
  "clp",
  "djf",
  "gnf",
  "jpy",
  "kmf",
  "krw",
  "mga",
  "pyg",
  "rwf",
  "ugx",
  "vnd",
  "vuv",
  "xaf",
  "xof",
  "xpf",
]);

function minorUnitsPerMajorUnit(currency: string): number {
  return ZERO_DECIMAL_CURRENCIES.has(currency.toLowerCase()) ? 1 : 100;
}

/** A Stripe amount in the currency's major units, ready to format. */
export function toMajorUnits(amount: number, currency: string): number {
  return amount / minorUnitsPerMajorUnit(currency);
}

/**
 * Whether a typed amount is a price Stripe can bill as written: a whole number
 * for a zero-decimal currency, at most two decimals otherwise, and at least
 * one minor unit. Rounding instead would bill a client a different price than
 * the coach typed.
 */
export function isValidAmountForCurrency(
  amount: number,
  currency: string,
): boolean {
  if (!Number.isFinite(amount)) return false;
  const minor = amount * minorUnitsPerMajorUnit(currency);
  const isWholeMinorUnit = Math.abs(minor - Math.round(minor)) < 1e-6;
  return isWholeMinorUnit && Math.round(minor) > 0;
}

/** What to tell the coach when `isValidAmountForCurrency` refuses an amount. */
export function invalidAmountMessage(currency: string): string {
  if (minorUnitsPerMajorUnit(currency) === 1) {
    const name =
      currency.toLowerCase() === "jpy" ? "Yen" : currency.toUpperCase();
    return `${name} amounts must be whole numbers.`;
  }
  return "Amounts can have at most 2 decimal places.";
}

/** The `step` a number input needs so only valid amounts are offered. */
export function amountInputStep(currency: string): number {
  return minorUnitsPerMajorUnit(currency) === 1 ? 1 : 0.01;
}

/**
 * A typed amount as the whole number Stripe takes, e.g. $45.50 → 4550 and
 * ¥45000 → 45000. Callers refuse amounts finer than the smallest unit first.
 */
export function toMinorUnits(amount: number, currency: string): number {
  return Math.round(amount * minorUnitsPerMajorUnit(currency));
}

/** A Stripe amount as currency text, e.g. "$100.00" or "¥1,000". */
export function formatStripeAmount(
  amount: number,
  currency: string,
  options: Intl.NumberFormatOptions = {},
): string {
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: currency.toUpperCase(),
    ...options,
  }).format(toMajorUnits(amount, currency));
}

/** A Stripe amount exactly, without cents when it has none: "$100", "$74.50". */
export function formatExactStripeAmount(
  amount: number,
  currency: string,
): string {
  return formatStripeAmount(
    amount,
    currency,
    Number.isInteger(toMajorUnits(amount, currency))
      ? { minimumFractionDigits: 0, maximumFractionDigits: 0 }
      : {},
  );
}
